import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import Breadcrumbs from "@/components/Breadcrumbs";
import Loading from "@/components/Loading";
import MainActions from "@/components/MainActions";
import Page from "@/components/Page";
import VLFCard from "@/components/Card";
import VLFTextField from "@/components/Input/TextField";

import {MatchAPI} from "@/api/match";

import {
	MATCH_ADVANCED_CONTEXT_SECTIONS,
	MATCH_ADVANCED_FOR_AGAINST_SECTIONS,
	MATCH_ADVANCED_SIMPLE_SECTIONS,
	FOR_AGAINST_TYPES,
	MATCH_HALVES,
	createAdvancedStatsInitialValues,
	buildAdvancedStatsPayload,
} from "@/config/matchAdvancedStats";

import {SUCCESSFUL_OPERATION, ERROR_OPERATION} from "@/utils/constants";

import "./index.scss";

function MatchAdvancedStatsFormPage({handleNotification}) {
	const navigate = useNavigate();
	const {id} = useParams();

	const [loading, setLoading] = useState(false);
	const [values, setValues] = useState(createAdvancedStatsInitialValues());

	const breadcrumbsElements = [
		{label: "Partite", path: "/matches"},
		{label: "/"},
		{label: "Statistiche avanzate"},
	];

	const loadData = async () => {
		setLoading(true);
		try {
			const response = await MatchAPI.getAdvancedStats(id);
			setValues(createAdvancedStatsInitialValues(response || {}));
		} catch (error) {
			if (error?.response?.status !== 404) {
				handleNotification(ERROR_OPERATION, "error");
			} else {
				setValues(createAdvancedStatsInitialValues());
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadData();
	}, [id]);

	const handleInputChange = (field) => (event) => {
		const {value} = event.target;
		setValues((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleSubmit = async () => {
		setLoading(true);
		try {
			const payload = buildAdvancedStatsPayload(values);
			await MatchAPI.updateAdvancedStats(id, payload);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate(`/matches/${id}`, {replace: true});
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate(-1),
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
		},
	];

	const renderForAgainstCard = (section) => {
		return (
			<VLFCard
				key={section.title}
				title={section.title}
				openable={false}
				content={
					<div className="advanced-stats-card for-against">
						<div className="advanced-stats-row header">
							<div className="label">Statistica</div>
							{MATCH_HALVES.map((half, index) =>
								FOR_AGAINST_TYPES.map((type) => (
									<div key={`header-${half}-${type.suffix}`} className="cell">
										{index === 0 ? "1T" : "2T"} {type.label}
									</div>
								)),
							)}
						</div>
						{section.metrics.map((metric) => (
							<div key={metric.key} className="advanced-stats-row">
								<div className="label">{metric.label}</div>
								{MATCH_HALVES.map((half) =>
									FOR_AGAINST_TYPES.map((type) => {
										const field = `${metric.key}${type.suffix}${half}`;
										return (
											<div key={field} className="cell">
												<VLFTextField
													type="number"
													inputProps={{
														step:
															metric.decimals && metric.decimals > 0
																? 10 ** -metric.decimals
																: 1,
													}}
													value={values[field]}
													onChange={handleInputChange(field)}
													placeholder="0"
													onlyLabel
												/>
											</div>
										);
									}),
								)}
							</div>
						))}
					</div>
				}
			/>
		);
	};

	const renderContextCard = (section) => {
		return (
			<VLFCard
				key={section.title}
				title={section.title}
				openable={false}
				content={
					<div className="advanced-stats-card context">
						<div className="advanced-stats-row header">
							<div className="label">Statistica</div>
							{MATCH_HALVES.map((half, index) => (
								<div key={`${section.title}-half-${half}`} className="cell">
									{index === 0 ? "1° tempo" : "2° tempo"}
								</div>
							))}
						</div>
						{section.metrics.map((metric) => (
							<div key={metric.key} className="context-group">
								<div className="group-title">{metric.label}</div>
								{metric.contexts.map((context) => (
									<div key={`${metric.key}-${context.suffix}`} className="advanced-stats-row">
										<div className="label">{context.label}</div>
										{MATCH_HALVES.map((half) => {
											const field = `${metric.key}${context.suffix}${half}`;
											return (
												<div key={field} className="cell">
													<VLFTextField
														type="number"
														inputProps={{
															step:
																metric.decimals && metric.decimals > 0
																	? 10 ** -metric.decimals
																	: 1,
														}}
														value={values[field]}
														onChange={handleInputChange(field)}
														placeholder="0"
														onlyLabel
													/>
												</div>
											);
										})}
									</div>
								))}
							</div>
						))}
					</div>
				}
			/>
		);
	};

	const renderSimpleCard = (section) => {
		return (
			<VLFCard
				key={section.title}
				title={section.title}
				openable={false}
				content={
					<div className="advanced-stats-card simple">
						<div className="advanced-stats-row header">
							<div className="label">Statistica</div>
							{MATCH_HALVES.map((half, idx) => (
								<div key={`simple-${half}`} className="cell">
									{idx === 0 ? "1° tempo" : "2° tempo"}
								</div>
							))}
						</div>
						{section.metrics.map((metric) => (
							<div key={metric.key} className="advanced-stats-row">
								<div className="label">{metric.label}</div>
								{MATCH_HALVES.map((half) => {
									const field = `${metric.key}${half}`;
									return (
										<div key={field} className="cell">
											<VLFTextField
												type="number"
												inputProps={{
													step:
														metric.decimals && metric.decimals > 0
															? 10 ** -metric.decimals
															: 1,
												}}
												value={values[field]}
												onChange={handleInputChange(field)}
												placeholder="0"
												onlyLabel
											/>
										</div>
									);
								})}
							</div>
						))}
					</div>
				}
			/>
		);
	};

	return (
		<Page className="page-container match-advanced-stats-page">
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current="Inserimento dati"
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="row no-margin g-2">
				<div className="col-sm-12 padding-small">
					<div className="advanced-section-title">Indicatori For/Against</div>
					<div className="advanced-section-grid">
						{MATCH_ADVANCED_FOR_AGAINST_SECTIONS.map((section) =>
							renderForAgainstCard(section),
						)}
					</div>
				</div>
				<div className="col-sm-12 padding-small">
					<div className="advanced-section-title">
						Perdite e possesso palla
					</div>
					<div className="advanced-section-grid">
						{MATCH_ADVANCED_CONTEXT_SECTIONS.map((section) =>
							renderContextCard(section),
						)}
					</div>
				</div>
				<div className="col-sm-12 padding-small">
					<div className="advanced-section-title">
						Statistiche squadra e portiere
					</div>
					<div className="advanced-section-grid">
						{MATCH_ADVANCED_SIMPLE_SECTIONS.map((section) =>
							renderSimpleCard(section),
						)}
					</div>
				</div>
			</div>
		</Page>
	);
}

export default MatchAdvancedStatsFormPage;

